import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { TableCell, Icon, Tooltip, useTheme } from "@material-ui/core";
import { AutoSizer, Column, Table } from "react-virtualized";
import DoneIcon from "@material-ui/icons/Done";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import "react-virtualized/styles.css";

const VirtualizedTable = ({ headerHeight = 48, rowHeight = 48, ...props }) => {
  const { columns, onRowClick, ...tableProps } = props;
  const { palette } = useTheme();

  const cellRenderer = ({ cellData, columnIndex }) => {
    let data = cellData;
    if (cellData === "Pending" || cellData === "Printed") {
      data = (
        <Tooltip title={cellData}>
          <Icon aria-label={cellData} style={{ marginLeft: "2rem" }}>
            <Icon
              icon={
                cellData === "Printed" ? <DoneIcon /> : <HourglassEmptyIcon />
              }
              color={palette.success.main}
            />
          </Icon>
        </Tooltip>
      );
    }

    return (
      <>
        <StyledTableCell
          component="div"
          variant="body"
          style={{ height: rowHeight }}
          align="left"
          colour={palette.success.main}
        >
          {data}
        </StyledTableCell>
      </>
    );
  };

  const headerRenderer = ({ label, columnIndex }) => {
    return (
      <StyledTableHeaderCell
        component="div"
        variant="head"
        style={{ height: headerHeight }}
        align={columns[columnIndex].numeric || false ? "right" : "left"}
        colour={palette.success.main}
      >
        <span>{label}</span>
      </StyledTableHeaderCell>
    );
  };

  return (
    <AutoSizer>
      {({ height, width }) => (
        <StyledTable
          height={height}
          width={width}
          rowHeight={rowHeight}
          gridStyle={{
            direction: "inherit",
          }}
          headerHeight={headerHeight}
          {...tableProps}
        >
          {columns.map(({ dataKey, ...other }, index) => {
            return (
              <Column
                key={dataKey}
                headerRenderer={(headerProps) =>
                  headerRenderer({
                    ...headerProps,
                    columnIndex: index,
                  })
                }
                cellRenderer={cellRenderer}
                dataKey={dataKey}
                {...other}
              />
            );
          })}
        </StyledTable>
      )}
    </AutoSizer>
  );
};

VirtualizedTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      dataKey: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      numeric: PropTypes.bool,
      width: PropTypes.number.isRequired,
    })
  ).isRequired,
  headerHeight: PropTypes.number,
  onRowClick: PropTypes.func,
  rowHeight: PropTypes.number,
};

export const ReactVirtualizedTable = ({ data, columns, height = 200 }) => {
  return (
    <div style={{ height: height, width: "100%" }}>
      <VirtualizedTable
        rowCount={data.length}
        rowGetter={({ index }) => data[index]}
        columns={columns}
      />
    </div>
  );
};

const StyledTable = styled(Table)`
  .ReactVirtualized__Table__headerRow {
    border-top: 1px solid;
    border-bottom: 1px solid;
  }
`;

const StyledTableCell = styled(TableCell)`
  color: ${(props) => props.colour};
`;

const StyledTableHeaderCell = styled(TableCell)`
  font-size: 1.1rem;
  line-height: 1.1;
  text-align: center;
  color: ${(props) => props.colour};
  font-weight: 700;
`;
